import React, { useState, useEffect } from 'react';
import api from '../utils/axios';
import toast from 'react-hot-toast';
import {
    FiCheck,
    FiX,
    FiClock,
    FiUser,
    FiMail,
    FiPhone,
    FiMapPin,
    FiGlobe,
    FiFileText,
    FiBriefcase,
    FiEye,
    FiChevronDown,
    FiChevronUp,
    FiRefreshCw,
    FiAlertCircle,
    FiCheckCircle,
} from 'react-icons/fi';

const AgencyUpdateRequests = () => {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('pending');
    const [expandedRequest, setExpandedRequest] = useState(null);
    const [processingId, setProcessingId] = useState(null);
    const [rejectReason, setRejectReason] = useState('');
    const [showRejectModal, setShowRejectModal] = useState(null);

    useEffect(() => {
        fetchRequests();
    }, [filter]);

    const fetchRequests = async () => {
        try {
            setLoading(true);
            const response = await api.get(`/api/agency-updates?status=${filter}`);
            setRequests(response.data.data || []);
        } catch (error) {
            console.error('Error fetching requests:', error);
            toast.error('Erreur lors du chargement des demandes');
        } finally {
            setLoading(false);
        }
    };

    const handleApprove = async (requestId) => {
        try {
            setProcessingId(requestId);
            await api.put(`/api/agency-updates/${requestId}/approve`);
            toast.success('Modification approuvée avec succès');
            fetchRequests();
        } catch (error) {
            console.error('Error approving request:', error);
            toast.error(error.response?.data?.message || 'Erreur lors de l\'approbation');
        } finally {
            setProcessingId(null);
        }
    };

    const handleReject = async (requestId) => {
        try {
            setProcessingId(requestId);
            await api.put(`/api/agency-updates/${requestId}/reject`, {
                reason: rejectReason || 'Aucune raison fournie'
            });
            toast.success('Modification rejetée');
            setShowRejectModal(null);
            setRejectReason('');
            fetchRequests();
        } catch (error) {
            console.error('Error rejecting request:', error);
            toast.error(error.response?.data?.message || 'Erreur lors du rejet');
        } finally {
            setProcessingId(null);
        }
    };

    const formatDate = (date) => {
        return new Date(date).toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const renderFieldComparison = (label, icon, currentValue, newValue) => {
        const hasChanged = currentValue !== newValue;

        return (
            <div className={`p-3 rounded-lg ${hasChanged ? 'bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800' : 'bg-gray-50 dark:bg-gray-700/50'}`}>
                <div className="flex items-center gap-2 mb-1">
                    {icon}
                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{label}</span>
                    {hasChanged && (
                        <span className="ml-auto px-2 py-0.5 bg-yellow-100 dark:bg-yellow-900 text-yellow-700 dark:text-yellow-300 text-xs rounded-full">
                            Modifié
                        </span>
                    )}
                </div>
                {hasChanged ? (
                    <div className="space-y-1">
                        <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-400">Actuel:</span>
                            <span className="text-sm text-gray-600 dark:text-gray-300 line-through">{currentValue || '-'}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-xs text-green-600 dark:text-green-400">Nouveau:</span>
                            <span className="text-sm font-medium text-green-700 dark:text-green-300">{newValue || '-'}</span>
                        </div>
                    </div>
                ) : (
                    <span className="text-sm text-gray-700 dark:text-gray-300">{currentValue || '-'}</span>
                )}
            </div>
        );
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center h-64 gap-4">
                <div className="relative">
                    <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-200 border-t-primary-600"></div>
                    <FiBriefcase className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-5 h-5 text-primary-600" />
                </div>
                <p className="text-gray-500 dark:text-gray-400 animate-pulse">Chargement des demandes...</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4">
                <div>
                    <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2 sm:gap-3">
                        <div className="p-1.5 sm:p-2 bg-gradient-to-br from-primary-500 to-primary-700 rounded-lg sm:rounded-xl shadow-lg">
                            <FiBriefcase className="w-5 h-5 sm:w-7 sm:h-7 text-white" />
                        </div>
                        Demandes de Modification
                    </h1>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                        Gérez les demandes de modification des agences
                    </p>
                </div>

                <button
                    onClick={fetchRequests}
                    className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-all text-sm"
                >
                    <FiRefreshCw className="w-4 h-4" />
                    Actualiser
                </button>
            </div>

            {/* Filter Tabs */}
            <div className="flex gap-2 p-1 bg-gray-100 dark:bg-gray-800 rounded-xl w-fit">
                {[
                    { key: 'pending', label: 'En attente', icon: FiClock, color: 'yellow' },
                    { key: 'approved', label: 'Approuvées', icon: FiCheckCircle, color: 'green' },
                    { key: 'rejected', label: 'Rejetées', icon: FiX, color: 'red' },
                ].map(tab => (
                    <button
                        key={tab.key}
                        onClick={() => setFilter(tab.key)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${filter === tab.key
                            ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                            : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                            }`}
                    >
                        <tab.icon className={`w-4 h-4 ${filter === tab.key ? `text-${tab.color}-500` : ''}`} />
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* Requests List */}
            {requests.length === 0 ? (
                <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 text-center">
                    <FiAlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                        Aucune demande {filter === 'pending' ? 'en attente' : filter === 'approved' ? 'approuvée' : 'rejetée'}
                    </h3>
                    <p className="text-gray-500 dark:text-gray-400">
                        {filter === 'pending' ? 'Toutes les demandes ont été traitées.' : 'Aucune demande trouvée dans cette catégorie.'}
                    </p>
                </div>
            ) : (
                <div className="space-y-4">
                    {requests.map((request) => (
                        <div
                            key={request._id}
                            className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden"
                        >
                            {/* Request Header */}
                            <div
                                className="p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                                onClick={() => setExpandedRequest(expandedRequest === request._id ? null : request._id)}
                            >
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-4">
                                        <div className="p-2 bg-primary-100 dark:bg-primary-900/40 rounded-lg">
                                            <FiBriefcase className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                                        </div>
                                        <div>
                                            <h3 className="font-semibold text-gray-900 dark:text-white">
                                                {request.agency?.company || request.agency?.name || 'Agence'}
                                            </h3>
                                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                                {request.agency?.email}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-3">
                                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${request.status === 'pending' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' :
                                            request.status === 'approved' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                                                'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                                            }`}>
                                            {request.status === 'pending' ? 'En attente' : request.status === 'approved' ? 'Approuvée' : 'Rejetée'}
                                        </span>
                                        <span className="text-xs text-gray-500 dark:text-gray-400">
                                            {formatDate(request.createdAt)}
                                        </span>
                                        {expandedRequest === request._id ? (
                                            <FiChevronUp className="w-5 h-5 text-gray-400" />
                                        ) : (
                                            <FiChevronDown className="w-5 h-5 text-gray-400" />
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Expanded Content */}
                            {expandedRequest === request._id && (
                                <div className="border-t border-gray-100 dark:border-gray-700">
                                    <div className="p-6">
                                        <h4 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-4">
                                            Comparaison des modifications
                                        </h4>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            {renderFieldComparison(
                                                'Nom',
                                                <FiUser className="w-4 h-4 text-gray-400" />,
                                                request.currentData?.name,
                                                request.requestedData?.name
                                            )}
                                            {renderFieldComparison(
                                                'Téléphone',
                                                <FiPhone className="w-4 h-4 text-gray-400" />,
                                                request.currentData?.phone,
                                                request.requestedData?.phone
                                            )}

                                            {/* Agence, Bureau, Entreprise */}
                                            {renderFieldComparison(
                                                'Agence',
                                                <FiBriefcase className="w-4 h-4 text-gray-400" />,
                                                request.currentData?.companyInfo?.agence || request.currentData?.company,
                                                request.requestedData?.companyInfo?.agence || request.requestedData?.company
                                            )}
                                            {renderFieldComparison(
                                                'Bureau',
                                                <FiBriefcase className="w-4 h-4 text-gray-400" />,
                                                request.currentData?.companyInfo?.bureau,
                                                request.requestedData?.companyInfo?.bureau
                                            )}


                                            {renderFieldComparison(
                                                'Adresse',
                                                <FiMapPin className="w-4 h-4 text-gray-400" />,
                                                request.currentData?.companyInfo?.adresse || request.currentData?.companyInfo?.address,
                                                request.requestedData?.companyInfo?.adresse || request.requestedData?.companyInfo?.address
                                            )}
                                            {renderFieldComparison(
                                                'Ville',
                                                <FiMapPin className="w-4 h-4 text-gray-400" />,
                                                request.currentData?.companyInfo?.ville || request.currentData?.companyInfo?.city,
                                                request.requestedData?.companyInfo?.ville || request.requestedData?.companyInfo?.city
                                            )}
                                        </div>

                                        {/* Rejection reason if rejected */}
                                        {request.status === 'rejected' && request.rejectionReason && (
                                            <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                                                <p className="text-sm font-medium text-red-700 dark:text-red-300 mb-1">Raison du rejet:</p>
                                                <p className="text-sm text-red-600 dark:text-red-400">{request.rejectionReason}</p>
                                            </div>
                                        )}

                                        {/* Actions for pending requests */}
                                        {request.status === 'pending' && (
                                            <div className="mt-6 flex gap-3">
                                                <button
                                                    onClick={() => handleApprove(request._id)}
                                                    disabled={processingId === request._id}
                                                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-green-600 hover:bg-green-700 text-white font-medium rounded-xl transition-all disabled:opacity-50"
                                                >
                                                    <FiCheck className="w-5 h-5" />
                                                    Approuver
                                                </button>
                                                <button
                                                    onClick={() => setShowRejectModal(request._id)}
                                                    disabled={processingId === request._id}
                                                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-red-600 hover:bg-red-700 text-white font-medium rounded-xl transition-all disabled:opacity-50"
                                                >
                                                    <FiX className="w-5 h-5" />
                                                    Rejeter
                                                </button>
                                            </div>
                                        )}

                                        {/* Processed by info */}
                                        {request.processedBy && (
                                            <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700 text-sm text-gray-500 dark:text-gray-400">
                                                Traité par {request.processedBy.name} le {formatDate(request.processedAt)}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Reject Modal */}
            {showRejectModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-md w-full">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                            Rejeter la demande
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                            Entrez une raison pour le rejet (optionnel):
                        </p>
                        <textarea
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value)}
                            className="w-full p-3 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-900 text-gray-900 dark:text-white resize-none"
                            rows={3}
                            placeholder="Raison du rejet..."
                        />
                        <div className="flex gap-3 mt-4">
                            <button
                                onClick={() => {
                                    setShowRejectModal(null);
                                    setRejectReason('');
                                }}
                                className="flex-1 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-all"
                            >
                                Annuler
                            </button>
                            <button
                                onClick={() => handleReject(showRejectModal)}
                                disabled={processingId === showRejectModal}
                                className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-xl transition-all disabled:opacity-50"
                            >
                                Confirmer le rejet
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AgencyUpdateRequests;
